<!-- Generated by Copilot -->
<template>
  <v-card class="request-statistics pa-4">
    <v-card-title class="text-h6">Request Statistics</v-card-title>
    <v-card-text v-if="stats && stats.length > 0">
      <!-- Simple Status Bar -->
      <v-card variant="outlined" class="pa-3 mb-4">
        <h4 class="mb-2">Request Status Overview</h4>
        <div class="status-bar-container">
          <div class="status-bar">
            <div 
              v-if="successRate > 0"
              class="status-segment success"
              :style="{ width: `${successRate}%` }"
              :title="`${successfulRequests} successful (${successRate}%)`"
            >
              <span v-if="successRate > 10" class="status-label">{{ successRate }}%</span>
            </div>
            <div 
              v-if="retriedPercent > 0"
              class="status-segment retried"
              :style="{ width: `${retriedPercent}%` }"
              :title="`${retriedRequests} retried from 503 (${retriedPercent}%)`"
            >
              <span v-if="retriedPercent > 10" class="status-label">{{ retriedPercent }}%</span>
            </div>
            <div 
              v-if="failureRate > 0"
              class="status-segment failed"
              :style="{ width: `${failureRate}%` }"
              :title="`${failedRequests} failed (${failureRate}%)`"
            >
              <span v-if="failureRate > 10" class="status-label">{{ failureRate }}%</span>
            </div>
          </div>
          <div class="status-legend mt-2">
            <span class="legend-item">
              <span class="legend-color success"></span> Success: {{ successfulRequests - retriedRequests }}
            </span>
            <span class="legend-item">
              <span class="legend-color retried"></span> Retried from 503: {{ retriedRequests }}
            </span>
            <span class="legend-item">
              <span class="legend-color failed"></span> Failed: {{ failedRequests }}
            </span>
          </div>
        </div>
      </v-card>
      
      <v-row>
        <!-- Overall Stats -->
        <v-col cols="12" md="6">
          <v-card variant="outlined" class="pa-3">
            <h4>Overall Performance</h4>
            <v-list density="compact">
              <v-list-item>
                <v-list-item-title>Total Requests:</v-list-item-title>
                <v-list-item-subtitle>{{ totalRequests }}</v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Successful:</v-list-item-title>
                <v-list-item-subtitle :class="successRateColor">
                  {{ successfulRequests }} ({{ successRate }}%)
                </v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Failed:</v-list-item-title>
                <v-list-item-subtitle class="text-error">
                  {{ failedRequests }} ({{ failureRate }}%)
                </v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Retried:</v-list-item-title>
                <v-list-item-subtitle>{{ retriedRequests }}</v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Total Samples:</v-list-item-title>
                <v-list-item-subtitle>{{ totalSamples }}</v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Unique Locations:</v-list-item-title>
                <v-list-item-subtitle>{{ uniqueLocations }}</v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>

        <!-- Error Breakdown -->
        <v-col cols="12" md="6">
          <v-card variant="outlined" class="pa-3">
            <h4>Error Breakdown</h4>
            <v-list density="compact">
              <v-list-item>
                <v-list-item-title>503 Errors (Service Unavailable):</v-list-item-title>
                <v-list-item-subtitle class="text-warning">
                  {{ error503Count }} ({{ error503Percent }}%)
                </v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>400 Errors (Bad Request):</v-list-item-title>
                <v-list-item-subtitle class="text-error">
                  {{ error400Count }} ({{ error400Percent }}%)
                </v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Other Errors:</v-list-item-title>
                <v-list-item-subtitle class="text-error">
                  {{ errorOtherCount }} ({{ errorOtherPercent }}%)
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
            
            <!-- Show details of "other" errors -->
            <div v-if="otherErrorDetails.length > 0" class="mt-3">
              <v-divider class="mb-2"></v-divider>
              <h5 class="text-caption mb-1">Other Error Details:</h5>
              <v-list density="compact" class="error-details-list">
                <v-list-item 
                  v-for="(errorDetail, index) in otherErrorDetails" 
                  :key="index"
                  class="text-caption"
                >
                  <v-list-item-title class="text-wrap">
                    <strong>{{ errorDetail.type }}:</strong> {{ errorDetail.message }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption">
                    Count: {{ errorDetail.count }}
                  </v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </div>
          </v-card>
        </v-col>

        <!-- Timing Statistics -->
        <v-col cols="12">
          <v-card variant="outlined" class="pa-3">
            <h4>Timing Statistics (ms)</h4>
            <v-row>
              <v-col cols="6" sm="3">
                <div class="text-caption">Min Duration</div>
                <div class="text-h6">{{ minDuration }}</div>
              </v-col>
              <v-col cols="6" sm="3">
                <div class="text-caption">Max Duration</div>
                <div class="text-h6">{{ maxDuration }}</div>
              </v-col>
              <v-col cols="6" sm="3">
                <div class="text-caption">Average Duration</div>
                <div class="text-h6">{{ avgDuration }}</div>
              </v-col>
              <v-col cols="6" sm="3">
                <div class="text-caption">Median Duration</div>
                <div class="text-h6">{{ medianDuration }}</div>
              </v-col>
            </v-row>
          </v-card>
        </v-col>

        <!-- Duration Histogram -->
        <v-col cols="12">
          <v-card variant="outlined" class="pa-3">
            <DurationHistogram 
              :stats="stats" 
              :bin-count="10"
              title="Request Duration Distribution (ms)"
            />
          </v-card>
        </v-col>

        <!-- Request Timeline -->
        <v-col cols="12" v-if="showTimeline && stats && stats.length > 0 && stats.length <= 200">
          <v-card variant="outlined" class="pa-3">
            <h4>Request Timeline</h4>
            <div class="text-caption mb-2">Duration visualization (showing {{ stats.length }} requests)</div>
            <div class="timeline-container">
              <div 
                v-for="(stat, index) in stats" 
                :key="index"
                :class="['timeline-bar', getStatusClass(stat)]"
                :style="{ width: getBarWidth(stat) + '%', minHeight: '4px' }"
                :title="getTooltip(stat)"
              >
              </div>
            </div>
          </v-card>
        </v-col>
        
        <!-- Message when too many requests for timeline -->
        <v-col cols="12" v-if="showTimeline && stats && stats.length > 200">
          <v-card variant="outlined" class="pa-3">
            <v-alert type="info" variant="tonal" density="compact">
              Timeline hidden: Too many requests ({{ stats.length }}). Use the Time Range Status List below for detailed view.
            </v-alert>
          </v-card>
        </v-col>
      </v-row>

      <!-- Clear Button -->
      <v-row>
        <v-col cols="12" class="text-center">
          <v-btn 
            color="error" 
            variant="outlined"
            @click="$emit('clear')"
          >
            Clear Statistics
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
    
    <v-card-text v-else>
      <v-alert type="info" variant="outlined">
        No request statistics available yet. Fetch some data to see statistics.
      </v-alert>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { RequestStats } from '@/esri/services/TempoDataService';
import DurationHistogram from './DurationHistogram.vue';

interface Props {
  stats: RequestStats[] | null;
  showTimeline?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showTimeline: true
});

defineEmits<{
  (e: 'clear'): void;
}>();

// Overall stats
const totalRequests = computed(() => props.stats?.length || 0);
const successfulRequests = computed(() => 
  props.stats?.filter(s => s.status === 'success').length || 0
);
const failedRequests = computed(() => totalRequests.value - successfulRequests.value);
const successRate = computed(() => 
  totalRequests.value > 0 
    ? Math.round((successfulRequests.value / totalRequests.value) * 100) 
    : 0
);
const failureRate = computed(() => 100 - successRate.value);
const retriedRequests = computed(() => 
  props.stats?.filter(s => s.retriedFrom503 === true).length || 0
);
const retriedPercent = computed(() => 
  totalRequests.value > 0 
    ? Math.round((retriedRequests.value / totalRequests.value) * 100) 
    : 0
);
const totalSamples = computed(() => 
  props.stats?.reduce((sum, s) => sum + s.sampleCount, 0) || 0
);

// Calculate unique locations from the stats
const uniqueLocations = computed(() => {
  if (!props.stats) return 0;
  // This is approximate since we don't have location data in stats
  // We can estimate based on sample count and requests
  return Math.round(totalSamples.value / Math.max(totalRequests.value, 1));
});

// Success rate color
const successRateColor = computed(() => {
  const rate = successRate.value;
  if (rate >= 95) return 'text-success';
  if (rate >= 85) return 'text-warning';
  return 'text-error';
});

// Error breakdown
const error503Count = computed(() => 
  props.stats?.filter(s => s.errorType === '503').length || 0
);
const error400Count = computed(() => 
  props.stats?.filter(s => s.errorType === '400').length || 0
);
const errorOtherCount = computed(() => 
  props.stats?.filter(s => s.errorType === 'other').length || 0
);

const error503Percent = computed(() => 
  totalRequests.value > 0 
    ? Math.round((error503Count.value / totalRequests.value) * 100) 
    : 0
);
const error400Percent = computed(() => 
  totalRequests.value > 0 
    ? Math.round((error400Count.value / totalRequests.value) * 100) 
    : 0
);
const errorOtherPercent = computed(() => 
  totalRequests.value > 0 
    ? Math.round((errorOtherCount.value / totalRequests.value) * 100) 
    : 0
);

// Get details of "other" errors with their messages
const otherErrorDetails = computed(() => {
  if (!props.stats) return [];
  
  const otherErrors = props.stats.filter(s => s.errorType === 'other');
  
  // Group by error message and count occurrences
  const errorMap = new Map<string, { type: string; message: string; count: number }>();
  
  otherErrors.forEach(stat => {
    const message = stat.errorMessage || 'Unknown error';
    const statusCode = stat.statusCode ? `HTTP ${stat.statusCode}` : 'Network Error';
    const key = `${statusCode}:${message}`;
    
    if (errorMap.has(key)) {
      errorMap.get(key)!.count++;
    } else {
      errorMap.set(key, {
        type: statusCode,
        message: message,
        count: 1
      });
    }
  });
  
  // Convert to array and sort by count (descending)
  return Array.from(errorMap.values()).sort((a, b) => b.count - a.count);
});

// Timing statistics
const minDuration = computed(() => {
  if (!props.stats || props.stats.length === 0) return 0;
  return Math.round(Math.min(...props.stats.map(s => s.duration)));
});

const maxDuration = computed(() => {
  if (!props.stats || props.stats.length === 0) return 0;
  return Math.round(Math.max(...props.stats.map(s => s.duration)));
});

const avgDuration = computed(() => {
  if (!props.stats || props.stats.length === 0) return 0;
  const sum = props.stats.reduce((acc, s) => acc + s.duration, 0);
  return Math.round(sum / props.stats.length);
});

const medianDuration = computed(() => {
  if (!props.stats || props.stats.length === 0) return 0;
  const sorted = [...props.stats].map(s => s.duration).sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return Math.round(
    sorted.length % 2 === 0 
      ? (sorted[mid - 1] + sorted[mid]) / 2 
      : sorted[mid]
  );
});

// Timeline helpers
function getStatusClass(stat: RequestStats): string {
  if (stat.status === 'success') return 'status-success';
  if (stat.errorType === '503') return 'status-503';
  if (stat.errorType === '400') return 'status-400';
  return 'status-error';
}

function getBarWidth(stat: RequestStats): number {
  if (maxDuration.value === 0) return 0;
  return (stat.duration / maxDuration.value) * 100;
}

function getTooltip(stat: RequestStats): string {
  const time = new Date(stat.timestamp).toLocaleTimeString();
  return `${time}: ${stat.status} - ${stat.duration.toFixed(0)}ms - ${stat.sampleCount} samples`;
}
</script>

<style scoped>
.timeline-container {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 200px;
  overflow-y: auto;
  min-height: 50px;
}

.timeline-bar {
  min-height: 4px;
  height: 20px;
  border-radius: 3px;
  transition: opacity 0.2s;
}

.timeline-bar:hover {
  opacity: 0.8;
}

.status-success {
  background-color: rgb(76, 175, 80);
}

.status-503 {
  background-color: rgb(255, 152, 0);
}

.status-400 {
  background-color: rgb(244, 67, 54);
}

.status-error {
  background-color: rgb(158, 158, 158);
}

.error-details-list {
  max-height: 150px;
  overflow-y: auto;
  background-color: rgba(244, 67, 54, 0.05);
  border-radius: 4px;
  padding: 4px;
}

.text-wrap {
  white-space: normal;
  word-break: break-word;
}

/* Status Bar Styles */
.status-bar-container {
  width: 100%;
}

.status-bar {
  display: flex;
  width: 100%;
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.status-segment {
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
  position: relative;
}

.status-segment:hover {
  opacity: 0.8;
}

.status-segment.success {
  background-color: rgb(76, 175, 80);
}

.status-segment.retried {
  background-color: rgb(255, 152, 0);
}

.status-segment.failed {
  background-color: rgb(244, 67, 54);
}

.status-label {
  color: white;
  font-weight: bold;
  font-size: 14px;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
}

.status-legend {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  font-size: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 2px;
  display: inline-block;
}

.legend-color.success {
  background-color: rgb(76, 175, 80);
}

.legend-color.retried {
  background-color: rgb(255, 152, 0);
}

.legend-color.failed {
  background-color: rgb(244, 67, 54);
}
</style>
